
import { FormData, FreeZoneScheduleData, TaxablePersonDetails, Elections } from '../types';

// Helper to safely parse string to float, defaulting to 0 if invalid
export const safeParseFloat = (value: string | undefined | null): number => {
  if (value === undefined || value === null || value.trim() === '') {
    return 0;
  }
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

export const getAccountingBasis = (details: TaxablePersonDetails): string => {
  if (details.filingAs === 'natural_person') return details.naturalPersonSpecifics?.accountingBasis || '';
  if (details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') return details.juridicalPersonSpecifics?.accountingBasis || '';
  if (details.filingAs === 'unincorporated_partnership') return details.unincorporatedPartnershipSpecifics?.accountingBasis || '';
  if (details.filingAs === 'tax_group_parent') return details.taxGroupSpecifics?.accountingBasis || '';
  return '';
};

export const calculateTaxableIncomeBeforeLossUtilisation = (formData: FormData): number => {
  let taxableIncome = safeParseFloat(formData.accounting.accountingIncome);

  // Step 4: Accounting Adjustments & Exempt Income
  const adj = formData.accountingAdjustments;
  if (adj.equityMethodInvestmentAccounting === 'yes') {
    taxableIncome += safeParseFloat(adj.equityMethodShareOfLosses);
    taxableIncome -= safeParseFloat(adj.equityMethodShareOfProfits);
  }

  if (adj.unincorporatedPartnershipSeparateTP === 'yes') {
    taxableIncome -= safeParseFloat(adj.unincorporatedPartnershipNetProfits);
    taxableIncome += safeParseFloat(adj.unincorporatedPartnershipNetLosses);
  }

  if (adj.incomeLossesNotInISRecognised === 'yes') {
    taxableIncome += safeParseFloat(formData.schedule22Other.totalIncomeAdjustmentNotInIS);
    taxableIncome -= safeParseFloat(formData.schedule22Other.totalLossAdjustmentNotInIS);
  }

  if (formData.elections.realisationBasis) {
    taxableIncome -= safeParseFloat(formData.schedule22Other.totalUnrealisedGainsExcluded);
    taxableIncome += safeParseFloat(formData.schedule22Other.totalUnrealisedLossesExcluded);
    taxableIncome += safeParseFloat(formData.schedule22Other.totalDeferredGainsRealised);
    taxableIncome -= safeParseFloat(formData.schedule22Other.totalDeferredLossesRealised);
    taxableIncome -= safeParseFloat(adj.depreciationAdjustmentsRealisationDecrease);
    taxableIncome += safeParseFloat(adj.depreciationAdjustmentsRealisationIncrease);
  }

  if (formData.elections.transitionalRulesImmovableProperty) {
    taxableIncome -= safeParseFloat(formData.schedule21TransitionalRules.totalAmountExcludedImmovableProperty);
  }
  
  if (formData.elections.transitionalRulesIntangibleAssets) {
    taxableIncome -= safeParseFloat(formData.schedule21TransitionalRules.totalAmountExcludedIntangibleAssets);
  }
  if (formData.elections.transitionalRulesFinancialAssetsLiabilities) {
    const financialAssetAdjustment = safeParseFloat(formData.schedule21TransitionalRules.totalAmountExcludedFinancialAssets);
    const financialLiabilityAdjustment = safeParseFloat(formData.schedule21TransitionalRules.totalAmountExcludedFinancialLiabilities);
    taxableIncome = taxableIncome - financialAssetAdjustment + financialLiabilityAdjustment; 
  }


  if (adj.dividendsFromUaeResident === 'yes') {
    taxableIncome -= safeParseFloat(formData.schedule13UaeDividends.totalDividendsReceived);
  }
  if (adj.incomeLossesFromParticipatingInterest === 'yes') {
    taxableIncome -= safeParseFloat(formData.schedule18ParticipationExemption.totalIncomeExempted);
    taxableIncome += safeParseFloat(adj.incomeFromParticipatingInterestClawback); 
    taxableIncome -= safeParseFloat(adj.lossesFromParticipatingInterestClawback); 
  }
  if (formData.elections.foreignPermanentEstablishmentExemption) {
    const peIncomeOrLoss = safeParseFloat(formData.schedule14ForeignPE.netIncomeLossForAdjustment);
    taxableIncome -= peIncomeOrLoss; 
  }
  if (adj.incomeFromInternationalAircraftShippingNonResident === 'yes') {
    taxableIncome -= safeParseFloat(adj.incomeDerivedIntlAircraftShipping);
    taxableIncome += safeParseFloat(adj.expenditureIntlAircraftShipping);
  }

  // Step 5: Relief Applications
  const relief = formData.relief; 
  if (formData.elections.transfersWithinQualifyingGroup) {
      taxableIncome -= safeParseFloat(relief.totalGainLossNotTaxedQGCurrentPeriod);
      taxableIncome += safeParseFloat(relief.totalGainLossRecognisedClawbackQG);
  }
  if (formData.elections.businessRestructuringRelief) {
      taxableIncome -= safeParseFloat(relief.totalGainLossNotTaxedBRRCurrentPeriod);
      taxableIncome += safeParseFloat(relief.totalGainLossRecognisedClawbackBRR);
  }


  // Step 6: Other Adjustments
  const otherAdj = formData.otherAdjustments;
  taxableIncome += safeParseFloat(otherAdj.nonDeductibleEntertainmentExp);
  taxableIncome += safeParseFloat(otherAdj.nonDeductiblePensionContributions);
  taxableIncome += safeParseFloat(otherAdj.nonDeductibleCharitableDonationsNonQPBE);
  taxableIncome += safeParseFloat(otherAdj.expenditureDerivingExemptIncomeNonInterest);
  taxableIncome += safeParseFloat(otherAdj.ownerDividendsExpensed);
  if (formData.taxablePersonDetails.filingAs === 'natural_person') { 
    taxableIncome += safeParseFloat(otherAdj.naturalPersonWithdrawals);
  }
  taxableIncome += safeParseFloat(otherAdj.expensesNotWhollyExclusivelyBusiness);
  taxableIncome += safeParseFloat(otherAdj.otherNonDeductibleExpenditure);


  if (otherAdj.netInterestExpExceedsThreshold === 'yes') {
    taxableIncome += safeParseFloat(formData.schedule19InterestCapping.finalDisallowedNIEForCurrentPeriod); 
  }
  taxableIncome -= safeParseFloat(formData.schedule19InterestCapping.nieUtilisedFromBroughtForward);
  taxableIncome += safeParseFloat(otherAdj.specificInterestNonDeductible);


  if (otherAdj.relatedPartyTransactionsExist === 'yes') {
    taxableIncome += safeParseFloat(formData.schedule16RelatedParty.totalUpwardAdjustments); 
    taxableIncome -= safeParseFloat(formData.schedule16RelatedParty.totalDownwardAdjustments);
    if(otherAdj.relatedPartyAssetGainLossRealised === 'yes') {
        taxableIncome += safeParseFloat(otherAdj.relatedPartyAssetGain); 
        taxableIncome -= safeParseFloat(otherAdj.relatedPartyAssetLoss); 
    }
  }
  if (otherAdj.connectedPersonsTransactionsExist === 'yes') {
    taxableIncome += safeParseFloat(formData.schedule16ConnectedPersons.totalAdjustments); 
  }
  
  if (otherAdj.investorInQIF === 'yes') {
    taxableIncome += safeParseFloat(otherAdj.qifNetInterestIncome);
    taxableIncome += safeParseFloat(otherAdj.qifNetRentalIncome);
    taxableIncome += safeParseFloat(otherAdj.qifOtherIncome); 
    taxableIncome -= safeParseFloat(otherAdj.qifNetExemptIncome); 
    taxableIncome += safeParseFloat(otherAdj.qifDividendsDeclaredByTPFromQIF);
  }


  if (otherAdj.priorPeriodErrorCorrectionImpactAED10000OrLess === 'yes') {
    taxableIncome += safeParseFloat(otherAdj.priorPeriodErrorAdjustmentsIncreaseTI);
    taxableIncome -= safeParseFloat(otherAdj.priorPeriodErrorAdjustmentsDecreaseTI);
  }
  if (otherAdj.otherAdjustmentsNotCapturedAbove === 'yes') {
    taxableIncome += safeParseFloat(otherAdj.otherAdjustmentsNotCapturedIncreaseTI);
    taxableIncome -= safeParseFloat(otherAdj.otherAdjustmentsNotCapturedDecreaseTI);
  }

  return taxableIncome;
};

export const calculateTaxableIncomeAfterTaxLoss = (
  taxableIncomeBeforeLoss: number,
  lossesUtilisedStr: string | undefined
): number => {
  const lossesUtilisedInput = safeParseFloat(lossesUtilisedStr);

  if (taxableIncomeBeforeLoss <= 0) {
    return taxableIncomeBeforeLoss; 
  }

  const maxLossesToUtilise = taxableIncomeBeforeLoss * 0.75;
  const actualLossesUtilised = Math.min(lossesUtilisedInput, maxLossesToUtilise);
  
  const result = taxableIncomeBeforeLoss - actualLossesUtilised;
  return result; 
};


export const calculateCTLiabilityBeforeCredits = (
  taxableIncomeAfterTaxLoss: number,
  elections: Elections,
  taxablePersonDetails: TaxablePersonDetails,
  freeZoneScheduleData: FreeZoneScheduleData
): number => {
  if (elections.smallBusinessRelief) {
    return 0;
  }

  if (taxableIncomeAfterTaxLoss <= 0) {
    return 0;
  }
  
  const isFiledAsFZP = taxablePersonDetails.filingAs === 'free_zone_person';
  const isJuridicalAndFZP = taxablePersonDetails.filingAs === 'juridical_person' && taxablePersonDetails.juridicalPersonSpecifics?.isFreeZonePerson === 'yes';
  
  const isActualFZP = isFiledAsFZP || isJuridicalAndFZP;

  const electedToForego0Rate = taxablePersonDetails.juridicalPersonSpecifics?.electionToForego0RateFZ === 'yes';
  const meetsQualifyingReqs = taxablePersonDetails.juridicalPersonSpecifics?.meetsQualifyingFZRequirements === 'yes';
  const deMinimisMet = freeZoneScheduleData.deMinimisMetResult === true;

  const AED_THRESHOLD = 375000;
  const STANDARD_RATE = 0.09;
  const ZERO_RATE = 0;

  if (isActualFZP && !electedToForego0Rate && meetsQualifyingReqs && deMinimisMet) {
    const qualifyingIncome = safeParseFloat(freeZoneScheduleData.calculatedQualifyingIncome);
    const nonQualifyingIncome = safeParseFloat(freeZoneScheduleData.calculatedNonQualifyingIncome);
    
    const taxOnQualifyingIncome = qualifyingIncome * ZERO_RATE;

    let taxOnNonQualifyingIncome = 0;
    if (nonQualifyingIncome > AED_THRESHOLD) {
      taxOnNonQualifyingIncome = (nonQualifyingIncome - AED_THRESHOLD) * STANDARD_RATE;
    }
    // Note: The threshold effectively applies only to the income subject to 9%.
    // If Non-Qualifying Income itself is <= AED 375,000, it's taxed at 0%.
    // The total income (QI + NQI) might exceed 375k, but QI is explicitly 0%.
    
    return taxOnQualifyingIncome + taxOnNonQualifyingIncome;

  } else { 
    if (taxableIncomeAfterTaxLoss <= AED_THRESHOLD) {
      return 0;
    } else {
      return (taxableIncomeAfterTaxLoss - AED_THRESHOLD) * STANDARD_RATE;
    }
  }
};

export const calculateCorporateTaxPayable = (
  ctLiabilityBeforeCredits: number,
  taxCreditsAppliedStr: string | undefined
): number => {
  const taxCreditsApplied = safeParseFloat(taxCreditsAppliedStr);
  const payable = ctLiabilityBeforeCredits - taxCreditsApplied;
  return Math.max(0, payable); 
};
